/**
 * レポートデータ更新画面（AnalysisData）用ローカル Redux ストア。 URL
 * 由来キー・タスク画面データ・IDLE/LOADING/ERROR・インラインエラー・一括操作ローディングを集約する（SR-001,002,006〜015,019〜020）。
 */
import React from 'react'

import { produce } from 'immer'
import { Provider, useDispatch, useSelector, useStore } from 'react-redux'
import { createStore } from 'redux'

import { getMessage } from '../../../../common/utils/MessageUtils'

import './types'

/** @typedef {import('./types').DataCreateTaskScreenSlice} DataCreateTaskScreenSlice */
/** @typedef {import('./types').GenericDataKindItem} GenericDataKindItem */
/** @typedef {import('./types').AccountingUploadTaskItem} AccountingUploadTaskItem */
/** @typedef {import('./types').UploadFileListItem} UploadFileListItem */
/** @typedef {import('./types').GenericTaskScreenNormalized} GenericTaskScreenNormalized */

/** @typedef {'IDLE' | 'LOADING' | 'ERROR'} ReportDataScreenStatus */

/** 画面メイン状態リテラル（比較・初期値で使用） */
export const SCREEN_STATUS = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  ERROR: 'ERROR'
}

/**
 * @typedef {Object} ReportDataUpdateState
 * @property {string | number | null} dataCreateTaskKey - URL 由来タスクキー（SR-001）
 * @property {string | null} periodTypeFromUrl - URL 由来期間種別（SR-002）
 * @property {ReportDataScreenStatus} screenStatus - 画面メイン状態（SR-019）
 * @property {string | null} loadError - 初期取得等のエラーメッセージ
 * @property {Record<string, string>} inlineErrors - フィールドキー別インラインエラー（`pageAction` は画面上部の操作/API エラー）
 * @property {DataCreateTaskScreenSlice | null} dataCreateTask - Data_create_task 相当（operation_history 含む
 *   SR-013,014,015）
 * @property {GenericDataKindItem[]} genericDataKinds - SR-006
 * @property {Record<string, string>} statusByDataKind - SR-007
 * @property {Record<string, { updateUserName: string; updateDateTime: string }>} statusMetaByDataKind - SR-007 拡張:
 *   データ種別ごとのステータス変更日時・変更者
 * @property {AccountingUploadTaskItem[]} accountingUploadTasks - SR-010
 * @property {UploadFileListItem[]} uploadFileList - SR-012
 * @property {import('./types').ChrofyColumnGroup[]} allChrofyColumns - IF02090 由来の全 CHROFY 項目（Excel 出力用）
 * @property {boolean} mappingExportLoading - マッピング設定 Excel ダウンロード中
 * @property {boolean} mappingBulkLoading - マッピング一括 API 中（SR-060 等）
 * @property {boolean} validationBulkLoading - 検証一括 API 中（SR-063 等）
 * @property {boolean} actionLoading - データ更新/確定/確定解除など個別アクション API 中
 * @property {Record<string, boolean>} cardsExpandedByKind - データ種類カード展開状態（SR-028 支援）
 */

/** アクションタイプ定数（SCREAMING_SNAKE） */
export const ACTION_TYPES = {
  RESET_STATE: 'RESET_STATE',
  /** URL / Main から渡すルート初期値（dataCreateTaskKey, periodTypeFromUrl） */
  INIT: 'INIT',
  LOAD_START: 'LOAD_START',
  INIT_SUCCESS: 'INIT_SUCCESS',
  /** IF02090 全 CHROFY 項目（マッピング設定 Excel ダウンロード用） */
  SET_ALL_CHROFY_COLUMNS: 'SET_ALL_CHROFY_COLUMNS',
  SET_MAPPING_EXPORT_LOADING: 'SET_MAPPING_EXPORT_LOADING',
  REFRESH_TASK: 'REFRESH_TASK',
  LOAD_ERROR: 'LOAD_ERROR',
  SET_INLINE_ERROR: 'SET_INLINE_ERROR',
  CLEAR_INLINE_ERRORS: 'CLEAR_INLINE_ERRORS',
  SET_MAPPING_BULK_LOADING: 'SET_MAPPING_BULK_LOADING',
  SET_VALIDATION_BULK_LOADING: 'SET_VALIDATION_BULK_LOADING',
  SET_ACTION_LOADING: 'SET_ACTION_LOADING',
  SET_CARDS_EXPANDED: 'SET_CARDS_EXPANDED',
  TOGGLE_CARD_EXPANDED: 'TOGGLE_CARD_EXPANDED'
}

/** @type {ReportDataUpdateState} */
const initialState = {
  dataCreateTaskKey: null,
  periodTypeFromUrl: null,
  screenStatus: SCREEN_STATUS.IDLE,
  loadError: null,
  inlineErrors: {},
  dataCreateTask: null,
  genericDataKinds: [],
  statusByDataKind: {},
  statusMetaByDataKind: {},
  accountingUploadTasks: [],
  uploadFileList: [],
  allChrofyColumns: [],
  mappingExportLoading: false,
  mappingBulkLoading: false,
  validationBulkLoading: false,
  actionLoading: false,
  cardsExpandedByKind: {}
}

/**
 * 正規化済みタスク画面レスポンスをストアへ反映する（成功時は IDLE へ）
 *
 * @param {import('immer').WritableDraft<ReportDataUpdateState>} draft
 * @param {GenericTaskScreenNormalized} payload
 */
function _applyTaskScreenSuccess(draft, payload) {
  draft.dataCreateTask = payload.dataCreateTask
  draft.genericDataKinds = payload.genericDataKinds ?? []
  draft.statusByDataKind = payload.statusByDataKind ?? {}
  draft.statusMetaByDataKind = payload.statusMetaByDataKind ?? {}
  draft.accountingUploadTasks = payload.accountingUploadTasks ?? []
  draft.uploadFileList = payload.uploadFileList ?? []
  draft.screenStatus = SCREEN_STATUS.IDLE
  draft.loadError = null
}

/**
 * LoadError 用にメッセージ文字列へ正規化する。 意図的な日本語文字列はそのまま表示し、 Error/例外オブジェクトは英語の `err.message` を出さず E011012 へフォールバック（バグ #3228）。
 *
 * @param {unknown} err
 * @returns {string}
 */
function _toLoadErrorMessage(err) {
  if (typeof err === 'string' && err !== '') return err
  return getMessage('E011012')
}

/**
 * @param {ReportDataUpdateState | undefined} state
 * @param {{ type: string; payload?: unknown }} action
 * @returns {ReportDataUpdateState}
 */
export const reportDataUpdateReducer = (state = initialState, action) => {
  if (action.type === ACTION_TYPES.RESET_STATE) {
    return initialState
  }
  return produce(state, (draft) => {
    const { payload, type } = action

    switch (type) {
      case ACTION_TYPES.INIT: {
        const p = /** @type {{ dataCreateTaskKey?: string | number | null; periodTypeFromUrl?: string | null }} */ (
          payload ?? {}
        )
        if (p.dataCreateTaskKey !== undefined) {
          draft.dataCreateTaskKey = p.dataCreateTaskKey
        }
        if (p.periodTypeFromUrl !== undefined) {
          draft.periodTypeFromUrl = p.periodTypeFromUrl
        }
        break
      }

      case ACTION_TYPES.LOAD_START:
        draft.screenStatus = SCREEN_STATUS.LOADING
        draft.loadError = null
        break

      case ACTION_TYPES.INIT_SUCCESS:
      case ACTION_TYPES.REFRESH_TASK: {
        const screen = /** @type {GenericTaskScreenNormalized} */ (payload)
        _applyTaskScreenSuccess(draft, screen)
        break
      }

      case ACTION_TYPES.LOAD_ERROR:
        draft.screenStatus = SCREEN_STATUS.ERROR
        draft.loadError = _toLoadErrorMessage(payload)
        break

      case ACTION_TYPES.SET_INLINE_ERROR: {
        const p = /** @type {{ key: string; message: string }} */ (payload ?? { key: '', message: '' })
        if (p.key) {
          draft.inlineErrors[p.key] = p.message
        }
        break
      }

      case ACTION_TYPES.CLEAR_INLINE_ERRORS: {
        const p = /** @type {{ keys?: string[] } | undefined} */ (payload)
        if (p?.keys?.length) {
          for (const k of p.keys) {
            delete draft.inlineErrors[k]
          }
        } else {
          draft.inlineErrors = {}
        }
        break
      }

      case ACTION_TYPES.SET_ALL_CHROFY_COLUMNS:
        draft.allChrofyColumns = Array.isArray(payload) ? payload : []
        break

      case ACTION_TYPES.SET_MAPPING_EXPORT_LOADING:
        draft.mappingExportLoading = Boolean(payload)
        break

      case ACTION_TYPES.SET_MAPPING_BULK_LOADING:
        draft.mappingBulkLoading = Boolean(payload)
        break

      case ACTION_TYPES.SET_VALIDATION_BULK_LOADING:
        draft.validationBulkLoading = Boolean(payload)
        break

      case ACTION_TYPES.SET_ACTION_LOADING:
        draft.actionLoading = Boolean(payload)
        break

      case ACTION_TYPES.SET_CARDS_EXPANDED: {
        const next = /** @type {Record<string, boolean>} */ (payload && typeof payload === 'object' ? payload : {})
        // 展開マップを payload で丸ごと置換（{} で全折りたたみ）
        draft.cardsExpandedByKind = { ...next }
        break
      }

      case ACTION_TYPES.TOGGLE_CARD_EXPANDED: {
        const p = /** @type {{ kindCode: string }} */ (payload ?? { kindCode: '' })
        if (!p.kindCode) break
        const prev = draft.cardsExpandedByKind[p.kindCode] ?? false
        draft.cardsExpandedByKind[p.kindCode] = !prev
        break
      }

      default:
        break
    }
  })
}

/**
 * カスタム dispatch フック（type, payload 形式）
 *
 * @returns {(type: string, payload?: unknown) => void}
 */
export const useReportDataUpdateDispatch = () => {
  const _dispatch = useDispatch()
  return React.useCallback((type, payload) => _dispatch({ type, payload }), [_dispatch])
}

/** @type {import('react-redux').TypedUseSelectorHook<ReportDataUpdateState>} */
export const useReportDataUpdateSelector = useSelector

/** @type {() => import('redux').Store} */
export const useReportDataUpdateStore = useStore

/**
 * 本画面専用 Redux を子ツリーに供給する。ストアはモジュール単位のシングルトン（画面をまたいで共有しない想定）。
 *
 * @param {{ children: import('react').ReactNode }} props
 * @returns {JSX.Element}
 */
export const ReportDataUpdateProviderStore = (props) => {
  const [store] = React.useState(() => createStore(reportDataUpdateReducer))
  return <Provider store={store}>{props.children}</Provider>
}
